import { useMemo } from 'react';
import createStyles from '@guestyci/foundation/createStyles';
import Placeholder from '@guestyci/foundation/Placeholder';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import InfiniteScroll from '@guestyci/foundation/InfiniteScroll';
import { Col, Row } from '@guestyci/foundation/Layout';
import { Link } from 'react-router-dom';
import TextField from '@guestyci/foundation/TextField';
import cn from 'classnames';
import Highlighter from 'react-highlight-words';
import useCustomTextFieldStyles from 'app/shareable/theme/useCustomTextFieldStyles';
import ReservationPopup from './ReservationPopup';
import { ReservationView } from '../../hooks/reservations/useReservationsAPI';

const useLoadingClasses = createStyles(() => ({
  placeholderChildComp: {
    height: '2.1875rem',
    margin: '0.5rem 1rem',
    width: 'auto !important',
  },
}));

const useClasses = createStyles((theme: typeof defaultTheme) => ({
  box: {
    position: 'absolute',
    width: '100%',
    backgroundColor: theme.palette.background.white,
    left: 0,
    zIndex: 100,
    maxHeight: 500,
    borderRadius: '4px',
    top: 'calc(100% + 4px)',
    boxShadow:
      '0px 2px 4px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)',
  },
  highlight: {
    backgroundColor: theme.palette.alert.hover,
    padding: 0,
  },
  link: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
  bullSpan: {
    color: theme.palette.primary.disabled,
    padding: '0 .5rem',
  },
  item: {
    padding: '0 1rem',
    '&:hover': {
      backgroundColor: theme.palette.primary.background,
    },
  },
  cancelledText: {
    color: theme.palette.error.default,
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: 1.5,
  },
}));

function Loading() {
  const classes = useLoadingClasses();

  return (
    <Col>
      <Placeholder className={classes.placeholderChildComp} />
      <Placeholder className={classes.placeholderChildComp} />
    </Col>
  );
}

interface IListReservationsProps {
  list: ReservationView[];
  search: string;
  onLoadMore: (skip: number) => any;
  page: number;
  isFetching: boolean;
  isEmpty: boolean;
  isFocused: boolean;
  isVisible: boolean;
  onClickLink: () => void;
}

function ListReservations({
  list,
  search,
  onLoadMore,
  page,
  isFetching,
  isEmpty,
  isFocused,
  isVisible,
  onClickLink,
}: IListReservationsProps) {
  const textClasses = useCustomTextFieldStyles();
  const classes = useClasses();
  const PAGE_SIZE = 10;

  const totalCount = useMemo(() => {
    const { length } = list;
    const hasMore = PAGE_SIZE * page + PAGE_SIZE === length;
    return hasMore ? length + 10 : length;
  }, [list, page]);

  if (!isVisible) {
    return null;
  }

  if (isFocused) {
    return (
      <ReservationPopup
        title="Search for:"
        subTitle="reservation code or guest name"
      />
    );
  }

  if (isEmpty) {
    return (
      <ReservationPopup
        subTitle="Try using different keywords"
        title="We could’t find any matches"
      />
    );
  }

  return (
    <div className={classes.box}>
      <InfiniteScroll
        onLoadMore={onLoadMore}
        scrollOffset={100}
        pageSize={PAGE_SIZE}
        skip={page * PAGE_SIZE}
        totalCount={totalCount}
        isLoading={isFetching}
        data-qa="infinite-scroll"
      >
        {list.map(
          ({
            reservationId,
            linkProp,
            title,
            reservationCode,
            checkInDateFormatted,
            checkOutDateFormatted,
            roomAlias,
            isDeleted,
          }) => (
            <Col key={reservationId} className={classes.item}>
              <Link
                to={linkProp}
                className={classes.link}
                onClick={onClickLink}
              >
                <Row align="center" className="my-3">
                  <Col fullWidth>
                    <Row>
                      <TextField
                        className={cn(textClasses.darkenBoldSmallText, 'pr-2')}
                      >
                        <Highlighter
                          highlightClassName={classes.highlight}
                          searchWords={[search]}
                          autoEscape
                          textToHighlight={title}
                        />
                      </TextField>
                      <TextField className={textClasses.smallText}>
                        <Highlighter
                          highlightClassName={classes.highlight}
                          searchWords={[search]}
                          autoEscape
                          textToHighlight={reservationCode}
                        />
                      </TextField>
                    </Row>
                  </Col>

                  <Col className="ml-4">
                    {isDeleted && (
                      <TextField className={classes.cancelledText}>
                        cancelled
                      </TextField>
                    )}
                  </Col>
                </Row>
                <Row className="my-3">
                  <TextField className={textClasses.smallText}>
                    <span>{checkInDateFormatted}</span>
                    {' - '}
                    <span>{checkOutDateFormatted}</span>
                    <span className={classes.bullSpan}>&bull;</span>
                    <span>{roomAlias}</span>
                  </TextField>
                </Row>
              </Link>
            </Col>
          )
        )}
      </InfiniteScroll>
      {isFetching && <Loading />}
    </div>
  );
}

export default ListReservations;
